// BaseButton.tsx
import clsx from 'clsx';
import type { ButtonHTMLAttributes } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

export type BaseButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix' | 'suffix'>;

export const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(({ children, className, onClick, onKeyDown, ...props }, ref) => {
    const localRef = useRef<HTMLButtonElement>(null);

    /** Click & keyboard interaction */
    const handlePress = useCallback(
        (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
            onClick?.(e as React.MouseEvent<HTMLButtonElement>);
        },
        [onClick]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePress(e);
            }
            onKeyDown?.(e);
        },
        [handlePress, onKeyDown]
    );

    useImperativeHandle(ref, () => localRef.current!);

    return (
        <button
            ref={localRef}
            className={clsx('m-0 inline-flex appearance-none items-center justify-center gap-2 p-0 focus:ring-0 focus:outline-none', className)}
            onClick={handlePress}
            onKeyDown={handleKeyDown}
            data-interactive
            {...props}
        >
            {children}
        </button>
    );
});

BaseButton.displayName = 'BaseButton';
